import { useConfirm } from 'material-ui-confirm';
import {
  BetBoxContent,
  BetBoxSetion,
  StyledButton,
  StyledButtonGroup,
  StyledInputLabel,
  StyledTextField,
} from '@/components/features/casino/crash/BetBox/styles';
import { useTranslation } from 'next-i18next';
import { Divide, Multiply, Precision } from '@/utils/decimal/operations';
import { maxBetState } from '@/components/features/casino/crash/state';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Box, Typography } from '@mui/material';
import { StyledTokenIcon } from '@/components/ui/common/TokenIcon';
import { currentCurrencyState, walletInfoCurrentSelectedWallet } from '@/state/userInfo';
import { getCrashDecimal, getDecimal, getMinAmount } from '@/constants/decimal';
import { floorToNString } from '@/components/features/casino/crash/GameBox/utils';

enum BetOperate {
  Double,
  Half,
  Min,
  Max,
}

export default function CrashBetAmount({
  disabled,
  defaultAmount,
  onBetAmountChange,
  ssx = {},
  amountSx = {},
  children = null,
}) {
  const { t } = useTranslation();
  const [betAmount, setBetAmount] = useState(defaultAmount);
  const currentSelectedWallet = useAtomValue(walletInfoCurrentSelectedWallet);
  const userBalance = currentSelectedWallet?.balance
    ? Number(currentSelectedWallet?.balance)
    : 0;
  const currentCurrency = useAtomValue(currentCurrencyState);
  // test code
  // const userBalance = 2000;
  const MIN_BET_AMOUNT = getMinAmount(currentCurrency);
  const MAX_BET_AMOUNT = 10000;
  const handleBetAmount = (operate: BetOperate) => {
    // const bet = betAmount
    const maxBetAmount = Math.min(MAX_BET_AMOUNT, userBalance);
    let result = 0;
    const decimal = getCrashDecimal(currentCurrency);
    switch (operate) {
      case BetOperate.Double:
        result = Multiply(betAmount, 2, Precision.eight);
        if (result > maxBetAmount) {
          result = maxBetAmount;
        }
        setBetAmount(floorToNString(result, decimal));
        break;
      case BetOperate.Half:
        result = Divide(betAmount, 2, Precision.eight);
        if (result < MIN_BET_AMOUNT) {
          result = MIN_BET_AMOUNT;
        }
        setBetAmount(floorToNString(result, decimal));
        break;
      case BetOperate.Min:
        setBetAmount(MIN_BET_AMOUNT);
        break;
      case BetOperate.Max:
        handleMaxBet(maxBetAmount);
        break;
    }
  };

  useEffect(() => {
    if (betAmount < MIN_BET_AMOUNT) {
      setBetAmount(MIN_BET_AMOUNT);
    }
  }, [currentCurrency]);

  useEffect(() => {
    onBetAmountChange(betAmount);
  }, [betAmount]);

  const confirm = useConfirm();
  const enableMaxBet = useAtomValue(maxBetState);
  const handleMaxBet = (max: number) => {
    confirm({
      title: t('Max Bet'),
      description: t('Are you sure you Max Bet?'),
      confirmationText: t('YES,ALL IN!'),
      cancellationText: t('NO'),
    })
      .then(() => {
        // setValue('betAmount', max);
        setBetAmount(max);
      })
      .catch(() => {
        // cancel
      });
  };
  const showDollar = String(betAmount).length < 11;
  return (
    <BetBoxSetion sx={ssx}>
      <StyledInputLabel htmlFor="betAmount">
        <span>{t('Bet Amount')}</span>
        {children}
      </StyledInputLabel>
      <Box
        sx={{
          width: '100%',
          ...amountSx,
        }}
      >
        <BetBoxContent>
          <StyledTokenIcon />
          <NumericFormat
            disabled={disabled}
            inputMode="decimal"
            value={betAmount}
            allowNegative={false}
            decimalScale={getCrashDecimal(currentCurrency)}
            fixedDecimalScale={false}
            thousandSeparator=","
            customInput={StyledTextField}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (!floatValue) {
                return true;
              } else if (floatValue < 0) {
                return false;
              } else {
                return floatValue <= userBalance;
              }
            }}
            onValueChange={(values) => {
              if (values.floatValue) {
                setBetAmount(values.floatValue);
              }
            }}
          ></NumericFormat>
          {showDollar && <Typography>${betAmount}</Typography>}
        </BetBoxContent>

        <StyledButtonGroup
          sx={{ cursor: disabled ? 'not-allowed' : '', mt: 1 }}
        >
          <StyledButton
            disabled={disabled}
            color={'secondary'}
            variant="outlined"
            onClick={() => handleBetAmount(BetOperate.Half)}
          >
            1/2
          </StyledButton>
          <StyledButton
            disabled={disabled}
            variant="outlined"
            color={'secondary'}
            onClick={() => handleBetAmount(BetOperate.Double)}
          >
            2x
          </StyledButton>
          <StyledButton
            disabled={disabled}
            color={'secondary'}
            variant="outlined"
            onClick={() => handleBetAmount(BetOperate.Min)}
          >
            Min
          </StyledButton>
          {enableMaxBet && (
            <StyledButton
              disabled={disabled}
              color={'secondary'}
              variant="outlined"
              onClick={() => {
                handleBetAmount(BetOperate.Max);
              }}
            >
              Max
            </StyledButton>
          )}
        </StyledButtonGroup>
      </Box>
    </BetBoxSetion>
  );
}
